import { useState, useRef, useEffect } from 'react';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  PaymentTypeBadge,
  PAYMENT_TYPE_OPTIONS,
  paymentTypeMeta,
} from '@/components/ui/PaymentTypeBadge';
import { useUpdateBookingPaymentType } from '@/hooks/api/useFrontDesk';
import type { PaymentType } from '@/services/api/frontdesk.api';

/**
 * Editable payment-type control for a reservation. Shows the current value as a
 * badge and opens a small menu to reclassify it — calling
 * `PATCH /v1/bookings/:id/payment-type` and invalidating the relevant caches.
 */
export function PaymentTypeControl({
  bookingId,
  paymentType,
}: {
  bookingId: string;
  paymentType: PaymentType | null | undefined;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const mutation = useUpdateBookingPaymentType();
  const current: PaymentType | null = paymentType ?? null;

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  function handleSelect(value: PaymentType | null) {
    setOpen(false);
    if (value === current) return;
    mutation.mutate({ id: bookingId, paymentType: value });
  }

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={mutation.isPending}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Payment type: ${paymentTypeMeta(current).label}. Change`}
        className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {mutation.isPending ? (
          <Loader2 size={14} className="animate-spin text-gray-400" aria-hidden="true" />
        ) : (
          <PaymentTypeBadge paymentType={current} />
        )}
        <ChevronDown size={14} className="text-gray-400" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Set payment type"
          className="absolute left-0 z-20 mt-1 w-56 rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
        >
          {PAYMENT_TYPE_OPTIONS.map((opt) => {
            const active = opt.value === current;
            return (
              <button
                key={opt.label}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => handleSelect(opt.value)}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none',
                  active ? 'font-medium text-gray-900' : 'text-gray-600',
                )}
              >
                <span className="inline-flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className={cn('h-1.5 w-1.5 rounded-full', paymentTypeMeta(opt.value).dot)}
                  />
                  {opt.label}
                </span>
                {active && <Check size={14} className="text-primary-600" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}

      {mutation.isError && (
        <p role="alert" className="mt-1 text-xs text-red-600">
          Couldn&apos;t update payment type. Try again.
        </p>
      )}
    </div>
  );
}
